import * as React from 'react';
import { connect } from 'react-redux';
import { ComboBox, IComboBoxOption, PrimaryButton } from 'office-ui-fabric-react';
import { ApplicationState } from "../../../store/index";
import { actionCreators, IJiraConnectionState, IJiraUser, IJiraUserLinkInfo } from "../../../store/integration/JiraStore";
import { ILinkDto, IListStore, validateConnection, loadDataAfterConnectionValidation } from "../../../store/integration/common";
import JiraConnectionSelect from '../Jira/JiraConnectionSelect';
import OverlayComponent from "../../common/OverlayComponent";
import { ISelectableOption } from 'office-ui-fabric-react/lib/utilities/selectableOption/SelectableOption.types';
import { ConnectionField } from '../ConnectionField';
import { buildUserDisplayInfo } from './common';
import { FieldValue } from '../ConnectionFieldValue';

type OwnProps = {
    actions: {
        linkToUser: (linkData: ILinkDto<IJiraUser>) => void;
        deleteUserLink: (connectionId: string) => void;
    };
    connectionId?: string;
    sourceData?: IJiraUserLinkInfo;
    dismissPanel?: () => void;
}

type StateProps = {
    users: IListStore<IJiraUser>;
    connections: IJiraConnectionState;
};
type Props = StateProps & typeof actionCreators & OwnProps;

type State = {
    connectionId: string | undefined;
    accountId: string | undefined;
    displayName: string | undefined;
    email: string | undefined;
    users: IComboBoxOption[];
}

class JiraConnectControl extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            connectionId: this.props.connectionId,
            accountId: props.sourceData?.accountId,
            displayName: props.sourceData?.displayName,
            email: props.sourceData?.email,
            users: []
        };
    }

    componentWillReceiveProps(nextProps: Props) {
        if (this.props.users !== nextProps.users) {
            const users: IComboBoxOption[] = [];
            let displayName: string | undefined;
            let email: string | undefined;

            for (const user of nextProps.users.entities) {
                if (user.accountId === this.state.accountId) {
                    displayName = user.displayName;
                    email = user.email;
                }

                users.push({
                    key: user.accountId,
                    text: buildUserDisplayInfo(user.displayName, user.email),
                    disabled: user.isAlreadyLinked
                });
            }

            this.setState({ displayName, email, users });
        }

        loadDataAfterConnectionValidation(
            this.props.connections,
            nextProps.connections,
            this.state.connectionId,
            this.props.loadUsers
        )
    }

    public render() {
        const { displayName, email, connectionId } = this.state;
        const { sourceData } = this.props;

        const isLinked = !!this.props.sourceData?.accountId;
        const userDisplayInfo = displayName && email && buildUserDisplayInfo(displayName, email)
            || sourceData && buildUserDisplayInfo(sourceData.displayName, sourceData.email);

        const isValid = validateConnection(this.props.connections, connectionId);

        return <>
            <div key="select-connection-container" className="with-top-margin">
                <JiraConnectionSelect
                    disabled={isLinked}
                    connectionId={this.state.connectionId}
                    onChange={this._onConnectionChange} />
            </div>
            <div key="select-resource-container" className="with-top-margin">
                <OverlayComponent isOverlayed={!connectionId || isValid?.isValidating !== false || !!isValid?.validationError}>
                    <ConnectionField
                        label="Resource"
                        isLinked={isLinked}
                        isLoading={this.props.users.isLoading}
                        editor={ <ComboBox 
                            disabled={isLinked || !connectionId || this.props.users.isLoading}
                            placeholder="Select resource"
                            selectedKey={this.state.accountId}
                            useComboBoxAsMenuWidth
                            allowFreeform
                            options={this.state.users}
                            onRenderItem={this.onRenderItem}
                            onChange={(e, _) => this.setState({ accountId: _ && _.key as string })}
                            errorMessage={isValid?.isValidating === false && !isValid?.validationError ? this._getComboboxValidationMessage() : undefined } />
                        }
                        viewer={ <FieldValue value={userDisplayInfo} /> }
                    />
                    {
                        isLinked 
                            ? <PrimaryButton text="Delete resource link"
                                className="with-top-margin"
                                onClick={this._deleteUserLink} />
                            : <PrimaryButton text="Link resource"
                                disabled={this.state.accountId === undefined}
                                className="with-top-margin"
                                onClick={this._linkToUser} />
                    }
                </OverlayComponent>
            </div>
        </>;
    }

    private _onConnectionChange = (connectionId?: string) => {
        const isLinked = !!this.props.sourceData?.accountId;
        if (isLinked) return;

        this.setState({
            connectionId: connectionId,
            accountId: undefined,
            displayName: undefined,
            email: undefined
        });
    }

    private _linkToUser = () => {
        const { users, actions, dismissPanel } = this.props;
        const { connectionId, accountId } = this.state;
        if (!connectionId || !accountId) {
            return;
        }

        const user = users.entities.find(_ => _.accountId === accountId);
        if (!user) {
            return;
        }

        actions.linkToUser({ connectionId, data: user });
        dismissPanel && dismissPanel();
    }

    private _deleteUserLink = () => {
        const { connectionId } = this.state;
        if (!connectionId) return;

        this.props.actions.deleteUserLink(connectionId);
        this.setState({ accountId: undefined, displayName: undefined, email: undefined });
    }

    private _getComboboxValidationMessage = () => {
        if (!this.state.connectionId || this.props.users.isLoading) {
            return undefined;
        }

        return this.props.users.error
            ? this.props.users.error
            : !this.state.accountId
                ? `Please select resource from list`
                : undefined;
    }

    private onRenderItem = (props?: ISelectableOption, defaultRender?: (props?: ISelectableOption) => JSX.Element | null): JSX.Element | null => {
        if (!defaultRender) {
            return null;
        }

        let element = defaultRender(props);
        if (!props || !props.disabled) {
            return element;
        }
        return React.createElement("div", { title: "Already linked" }, element);
    }
}

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    return { 
        users: state.jira.users,
        connections: state.jira.connections
    };
}

export default connect(mapStateToProps, actionCreators)(JiraConnectControl);